import axios from 'axios';

// Use the configured backend URL (preview) by default, BUT fall back to same-origin
// when the app is served from a production custom domain like crm.uneedservices.fr.
// This avoids 3rd-party cookie issues — the backend is reverse-proxied on the same host.
const ENV_API_URL = process.env.REACT_APP_BACKEND_URL || '';
const isProdDomain = typeof window !== 'undefined' && /uneedservices\.fr$/i.test(window.location.hostname);
export const API_URL = isProdDomain ? '' : ENV_API_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    withCredentials: true,
    timeout: 120000, // 2 min — accommodate large PDF uploads
    headers: {
        'Content-Type': 'application/json'
    }
});

// Prospects/Clients
export const getProspects = (params = {}) => api.get('/prospects', { params });
export const getProspect = (id) => api.get(`/prospects/${id}`);
export const getProspectChildren = (id) => api.get(`/prospects/${id}/children`);
export const createProspect = (data) => api.post('/prospects', data);
export const updateProspect = (id, data) => api.put(`/prospects/${id}`, data);
export const deleteProspect = (id) => api.delete(`/prospects/${id}`);
export const bulkProspects = (action, ids, payload = null) =>
    api.post('/prospects/bulk', { action, ids, payload });

// Dossiers
export const getDossiers = (params = {}) => api.get('/dossiers', { params });
export const getDossier = (id) => api.get(`/dossiers/${id}`);
export const createDossier = (data) => api.post('/dossiers', data);
export const updateDossier = (id, data) => api.put(`/dossiers/${id}`, data);
export const deleteDossier = (id) => api.delete(`/dossiers/${id}`);
export const getPipeline = () => api.get('/dossiers/pipeline');
export const bulkDossiers = (action, ids, payload = null) =>
    api.post('/dossiers/bulk', { action, ids, payload });

// Cover photo (dossier)
export const uploadCoverPhoto = (dossierId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/dossiers/${dossierId}/cover-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};
export const getCoverPhotoUrl = (dossierId) => `${API_URL}/api/dossiers/${dossierId}/cover-photo`;

// Project costs (Fiche Budget)
export const getCosts = (dossierId, categorie = null) => api.get(`/costs/${dossierId}`, categorie ? { params: { categorie } } : undefined);
export const createCost = (data, file = null) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') fd.append(k, v);
    });
    if (file) fd.append('file', file);
    return api.post('/costs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateCost = (id, data) => api.put(`/costs/${id}`, data);
export const deleteCost = (id) => api.delete(`/costs/${id}`);
export const getCostInvoiceUrl = (costId) => `${API_URL}/api/costs/${costId}/invoice`;

// Project invoices (Fiche Facturation)
export const getInvoices = (dossierId) => api.get(`/invoices/${dossierId}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const uploadInvoicePdf = (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/invoices/${id}/pdf`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteInvoicePdf = (id) => api.delete(`/invoices/${id}/pdf`);
export const getInvoicePdfUrl = (id) => `${API_URL}/api/invoices/${id}/pdf`;

// Comments (chantier horodatés)
export const getComments = (dossierId) => api.get(`/comments/${dossierId}`);
export const createComment = (data) => api.post('/comments', data);

// Interactions
export const getInteractions = (dossierId) => api.get(`/interactions/${dossierId}`);
export const createInteraction = (data) => api.post('/interactions', data);

// Relances
export const getRelances = (params = {}) => api.get('/relances', { params });
export const createRelance = (data) => api.post('/relances', data);
export const updateRelance = (id, data) => api.put(`/relances/${id}`, data);
export const deleteRelance = (id) => api.delete(`/relances/${id}`);

// Documents
export const uploadDocument = (dossierId, typeDocument, file, commentaire = '', onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/upload?dossier_id=${dossierId}&type_document=${typeDocument}&commentaire=${commentaire}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min for large PDF uploads
        onUploadProgress: onProgress ? (evt) => {
            if (evt.total) {
                const pct = Math.round((evt.loaded * 100) / evt.total);
                onProgress(pct, evt.loaded, evt.total);
            }
        } : undefined,
    });
};
export const deleteDocument = (id) => api.delete(`/documents/${id}`);

// Templates
export const getTemplates = (type = null) => api.get('/templates', { params: type ? { type_template: type } : {} });
export const createTemplate = (data) => api.post('/templates', data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`);

// Users
export const getUsers = () => api.get('/users');
export const getAssignableUsers = () => api.get('/users/assignable');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const bulkUsers = (action, ids) => api.post('/users/bulk', { action, ids });

// Link prospect to dossier (for orphan projects)
export const linkProspectToDossier = (dossierId, prospectId) =>
    api.post(`/dossiers/${dossierId}/link-prospect`, { prospect_id: prospectId });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Dashboard
export const getDashboardStats = (params = {}) => api.get('/dashboard/stats', { params });
export const getMyMoney = () => api.get('/dashboard/my-money');
export const getComplianceReport = () => api.get('/reports/compliance');
export const getUpcomingEvents = () => api.get('/dashboard/upcoming');
export const getHealthScore = () => api.get('/dashboard/health-score');
export const getCommerciauxStats = (commercialId = null) =>
    api.get('/dashboard/commerciaux', { params: commercialId ? { commercial_id: commercialId } : {} });

// Google Calendar
export const getCalendarStatus = () => api.get('/calendar/status');
export const getCalendarAuthUrl = () => api.get('/calendar/auth');
export const createCalendarEvent = (data) => api.post('/calendar/create-event', data);
export const disconnectCalendar = () => api.post('/calendar/disconnect');

// Config
export const getStatutsConfig = () => api.get('/config/statuts');
export const getBonusTiers = () => api.get('/config/bonus-tiers');

// Parrainages
export const getParrainages = () => api.get('/parrainages');

// Exports
export const downloadExport = (type, format, params = {}) => {
    const qs = new URLSearchParams({ format, ...params }).toString();
    const url = `${API_URL}/api/exports/${type}?${qs}`;
    window.open(url, '_blank');
};

// Overdue (En retard)
export const getOverdueSummary = () => api.get('/overdue/summary');
export const getOverdueRdv = () => api.get('/overdue/rdv-sans-devis');
export const getOverdueDevis = (days = null) =>
    api.get('/overdue/devis-sans-retour', { params: days ? { days } : {} });
export const getOverdueRelances = () => api.get('/overdue/relances');
export const getOverdueChantiers = () => api.get('/overdue/chantiers');
export const getOverdueClients = (days = null) =>
    api.get('/overdue/clients-silencieux', { params: days ? { days } : {} });
export const snoozeOverdue = (entityType, entityId, snoozeHours) =>
    api.post('/overdue/snooze', { entity_type: entityType, entity_id: entityId, snooze_hours: snoozeHours });
export const reassignOverdue = (dossierId, commercialId) =>
    api.post('/overdue/reassign', { dossier_id: dossierId, commercial_id: commercialId });
export const downloadOverdueExport = (category, format) => {
    const url = `${API_URL}/api/exports/overdue?category=${category}&format=${format}`;
    window.open(url, '_blank');
};

// Audit log
export const getAuditLog = (params = {}) => api.get('/audit-log', { params });

// Invoices: payments + avoirs
export const addInvoicePayment = (id, data) => api.post(`/invoices/${id}/payment`, data);
export const createAvoir = (id) => api.post(`/invoices/${id}/avoir`);

// PV Réception
export const createPVReception = (dossierId, data) => api.post(`/dossiers/${dossierId}/pv-reception`, data);
export const getPVReception = (dossierId) => api.get(`/dossiers/${dossierId}/pv-reception`);
export const savePvSignature = (pvId, payload) => api.post(`/pv-receptions/${pvId}/signature`, payload);
export const getPvSignatureUrl = (pvId, role) => `${API_URL}/api/pv-receptions/${pvId}/signature/${role}`;
export const deletePvSignature = (pvId, role) => api.delete(`/pv-receptions/${pvId}/signature/${role}`);
export const updateReserve = (id, data) => api.put(`/reserves/${id}`, data);
export const listReserves = (params = {}) => api.get('/reserves', { params });

// Litiges
export const getLitiges = (params = {}) => api.get('/litiges', { params });
export const getLitige = (id) => api.get(`/litiges/${id}`);
export const createLitige = (data) => api.post('/litiges', data);
export const updateLitige = (id, data) => api.put(`/litiges/${id}`, data);
export const deleteLitige = (id) => api.delete(`/litiges/${id}`);
export const addLitigeAction = (id, data) => api.post(`/litiges/${id}/actions`, data);
export const uploadLitigeJustificatif = (id, file, description = '') => {
    const fd = new FormData();
    fd.append('file', file);
    if (description) fd.append('description', description);
    return api.post(`/litiges/${id}/justificatifs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteLitigeJustificatif = (id, justifId) =>
    api.delete(`/litiges/${id}/justificatifs/${justifId}`);
export const getLitigeJustificatifUrl = (id, justifId) =>
    `${API_URL}/api/litiges/${id}/justificatifs/${justifId}/download`;

// Imports (admin only)
const uploadImportFile = (path, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(path, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
    });
};
export const importProspectsPreview = (file) => uploadImportFile('/imports/prospects/preview', file);
export const importProspectsCommit = (payload) => api.post('/imports/prospects/commit', payload);
export const importProjectsPreview = (file) => uploadImportFile('/imports/projects/preview', file);
export const importProjectsCommit = (payload) => api.post('/imports/projects/commit', payload);

// Trash / Corbeille (admin only)
export const listTrashedProspects = () => api.get('/trash/prospects');
export const restoreProspect = (id) => api.post(`/trash/prospects/${id}/restore`);
export const hardDeleteProspect = (id) => api.delete(`/trash/prospects/${id}`);
export const listTrashedDossiers = () => api.get('/trash/dossiers');
export const restoreDossier = (id) => api.post(`/trash/dossiers/${id}/restore`);
export const hardDeleteDossier = (id) => api.delete(`/trash/dossiers/${id}`);

export default api;
